<template>
  <div class="checkout-container app-page">

    <div class="checkout-layout">
      <!-- 收货地址 -->
      <div class="checkout-section address-section">
        <div class="section-header">
          <div class="section-header-left">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <h3>收货地址</h3>
          </div>
          <el-button
            type="text"
            @click="showAllAddresses = !showAllAddresses"
            class="edit-address-btn"
          >
            {{ showAllAddresses ? '收起地址' : '修改地址' }}
          </el-button>
        </div>

        <!-- 地址内容容器 -->
        <div class="address-content">
          <!-- 默认只显示默认地址 -->
          <div v-if="!showAllAddresses && defaultAddress" class="default-address-card">
            <div class="address-card-left">
              <div class="address-avatar">{{ defaultAddress.receiver?.charAt(0) || '?' }}</div>
              <div class="address-info">
                <div class="address-header">
                  <span class="receiver">{{ defaultAddress.receiver }}</span>
                  <span class="phone">{{ defaultAddress.phone }}</span>
                  <span class="default-tag">默认</span>
                </div>
                <div class="address-detail">{{ defaultAddress.addressDetail }}</div>
              </div>
            </div>
          </div>

          <!-- 点击修改地址后展示所有地址供选择 -->
          <el-radio-group
            v-model="selectedAddressId"
            v-if="showAllAddresses || !defaultAddress"
            class="address-list"
          >
            <el-radio
              v-for="addr in addresses"
              :key="addr.id"
              :label="addr.id"
              class="address-item"
            >
              <div class="address-item-left">
                <div class="address-avatar" :class="{ default: addr.isDefault === 1 }">{{ addr.receiver?.charAt(0) || '?' }}</div>
                <div class="address-info-wrapper">
                  <div class="address-header">
                    <span class="receiver">{{ addr.receiver }}</span>
                    <span class="phone">{{ addr.phone }}</span>
                    <span v-if="addr.isDefault === 1" class="default-tag">默认</span>
                  </div>
                  <div class="address-detail">{{ addr.addressDetail }}</div>
                </div>
              </div>
            </el-radio>
          </el-radio-group>
        </div>

        <!-- 地址操作区域 -->
        <div class="address-actions">
          <el-button
            size="small"
            @click="router.push('/address/manage')"
            class="add-address-btn"
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            添加新地址
          </el-button>
        </div>
      </div>

      <!-- 商品清单 -->
      <div class="checkout-section products-section">
        <div class="section-header">
          <div class="section-header-left">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>
            <h3>商品清单</h3>
          </div>
        </div>
        <el-table
          :data="selectedProducts"
          style="width: 100%"
          class="products-table"
        >
          <el-table-column label="商品信息" min-width="300">
            <template #default="scope">
              <div class="product-info">
                <el-image
                  :src="scope.row.mainImage"
                  alt="商品图片"
                  class="product-img"
                ></el-image>
                <span class="product-name-text">{{ scope.row.productName }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="单价" width="120" align="right">
            <template #default="scope">¥{{ scope.row.price.toFixed(2) }}</template>
          </el-table-column>
          <el-table-column label="数量" width="100" align="center">
            <template #default="scope">{{ scope.row.quantity }}</template>
          </el-table-column>
          <el-table-column label="小计" width="120" align="right">
            <template #default="scope">
              <span class="subtotal-price">¥{{ (scope.row.price * scope.row.quantity).toFixed(2) }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 结算信息 -->
      <div class="summary-section">
        <div class="summary-inner">
          <div class="summary-rows">
            <div class="summary-item">
              <span>商品总价</span>
              <span>¥{{ totalPrice.toFixed(2) }}</span>
            </div>
            <div class="summary-item">
              <span>运费</span>
              <span class="free">免运费</span>
            </div>
            <div class="summary-divider"></div>
            <div class="summary-item total">
              <span>实付款</span>
              <span>¥{{ totalPrice.toFixed(2) }}</span>
            </div>
          </div>
          <el-button
            type="primary"
            size="large"
            @click="createOrder"
            :disabled="!selectedAddressId"
            class="submit-order-btn"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            提交订单
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// 脚本部分保持不变
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import cartApi from '@/api/cart'
import orderApi from '@/api/order'
import addressApi from '@/api/address'

const router = useRouter()
const route = useRoute()

const userId = localStorage.getItem('userId')
const cartItemIds = route.query.cartItemIds?.split(',') || []

const addresses = ref([])
const selectedAddressId = ref('')
const selectedProducts = ref([])
const showAllAddresses = ref(false)

const defaultAddress = computed(() => {
  return addresses.value.find(addr => addr.isDefault === 1) || addresses.value[0] || null
})

const totalPrice = computed(() => {
  return selectedProducts.value.reduce(
    (sum, item) => sum + (item.price * item.quantity),
    0
  )
})

watch(addresses, () => {
  if (defaultAddress.value) {
    selectedAddressId.value = defaultAddress.value.id
  }
}, { immediate: true })

onMounted(() => {
  if (!userId || cartItemIds.length === 0) {
    ElMessage.warning('参数错误，请重新选择商品')
    router.push('/cart')
    return
  }
  fetchAddresses()
  fetchSelectedProducts()
})

const fetchAddresses = async () => {
  try {
    const res = await addressApi.getAddressList(Number(userId))
    addresses.value = res.data.data || []
    if (addresses.value.length === 0) {
      ElMessage.warning('请先添加收货地址')
    }
  } catch (err) {
    console.error('获取地址失败:', err)
    ElMessage.error('获取地址失败，请稍后重试')
  }
}

const fetchSelectedProducts = async () => {
  try {
    const res = await cartApi.getCartList(Number(userId))
    if (res.data?.code == 200) {
      selectedProducts.value = res.data.data.filter(item =>
        cartItemIds.includes(item.id.toString())
      )
    } else {
      ElMessage.error(res.data?.msg || '获取商品信息失败')
    }
  } catch (err) {
    console.error('获取商品失败:', err)
    ElMessage.error('获取商品信息失败，请稍后重试')
  }
}

const createOrder = async () => {
  try {
    const address = addresses.value.find(addr => addr.id == selectedAddressId.value)
    if (!address) {
      ElMessage.warning('请选择收货地址')
      return
    }

    // 动态组装订单项：遍历选中的商品列表，映射为后端需要的格式
    const orderItems = selectedProducts.value.map(product => ({
      productId: product.productId,  // 商品ID
      count: product.quantity,       // 购买数量（对应商品的数量）
      price: product.price           // 商品单价
    }))

    const res = await orderApi.createOrder({
      userId: Number(userId),
      receiver: address.receiver,
      phone: address.phone,
      address: address.addressDetail,
      items: orderItems  // 使用动态生成的订单项
    })

    if (res.data?.code == 200) {
      const orderSn = res.data.data
      ElMessage.success('订单创建成功')
      router.push(`/order/pay/${orderSn}`)
    } else {
      ElMessage.error(res.data?.msg || '创建订单失败')
    }
  } catch (err) {
    console.error('创建订单失败:', err)
    ElMessage.error('创建订单失败，请稍后重试')
  }
}


</script>

<style scoped>
.checkout-container {
  max-width: 1000px;
  margin: 0 auto;
}

.checkout-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.checkout-section {
  background: var(--app-bg-card);
  border-radius: var(--app-radius-xl);
  box-shadow: var(--app-shadow-card);
  border: 1px solid var(--app-border-subtle);
  padding: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--app-border-light);
}

.section-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-header-left svg {
  color: var(--app-color-primary);
}

.section-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0;
}

.edit-address-btn {
  color: var(--app-color-primary) !important;
  font-weight: 500 !important;
}

/* Address styles */
.address-content {
  width: 100%;
  margin-bottom: 12px;
}

.default-address-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-lg);
  border: 1px solid var(--app-border-light);
}

.address-card-left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.address-avatar {
  width: 42px;
  height: 42px;
  border-radius: var(--app-radius-md);
  background: var(--app-gradient-primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.0625rem;
  flex-shrink: 0;
}

.address-avatar.default {
  background: var(--app-gradient-secondary);
}

.address-info, .address-info-wrapper {
  flex: 1;
}

.address-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 6px;
}

.receiver {
  font-weight: 600;
  color: var(--app-text-primary);
}

.phone {
  color: var(--app-text-secondary);
  font-size: 0.875rem;
}

.default-tag {
  background: var(--app-color-primary);
  color: #fff;
  font-size: 0.75rem;
  padding: 1px 8px;
  border-radius: var(--app-radius-full);
  font-weight: 500;
  margin-left: auto;
}

.address-detail {
  color: var(--app-text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
}

.address-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.address-item {
  display: block;
  padding: 0;
  cursor: pointer;
  position: relative;
  overflow: visible;
}

.address-item :deep(.el-radio__label) {
  width: 100%;
  padding: 0;
}

.address-item-left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px;
  border: 1px solid var(--app-border-light);
  border-radius: var(--app-radius-lg);
  transition: all 0.2s ease;
}

.address-item-left:hover {
  border-color: var(--app-color-primary-light);
  background: var(--app-color-primary-muted);
}

:deep(.el-radio.is-checked) .address-item-left {
  border-color: var(--app-color-primary);
  background: var(--app-color-primary-soft);
}

.address-actions {
  width: 100%;
  padding-top: 4px;
}

.add-address-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--app-color-primary) !important;
  border-color: transparent !important;
  background: var(--app-color-primary-soft) !important;
  border-radius: var(--app-radius-md) !important;
}

.add-address-btn:hover {
  background: rgba(255, 126, 126, 0.2) !important;
}

/* Products table */
.products-section {
  padding: 24px;
}

.products-table {
  border-radius: var(--app-radius-lg);
  overflow: hidden;
}

.products-table :deep(.el-table__header th) {
  background: var(--app-bg-soft) !important;
  color: var(--app-text-secondary);
  font-weight: 600;
}

.product-info {
  display: flex;
  align-items: center;
  gap: 14px;
}

.product-img {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border-light);
  flex-shrink: 0;
}

.product-name-text {
  font-weight: 500;
  color: var(--app-text-primary);
  font-size: 0.9375rem;
}

.subtotal-price {
  color: var(--app-color-primary);
  font-weight: 600;
}

/* Summary */
.summary-section {
  background: var(--app-bg-card);
  border-radius: var(--app-radius-xl);
  box-shadow: var(--app-shadow-card);
  border: 1px solid var(--app-border-subtle);
  padding: 24px;
}

.summary-inner {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}

.summary-rows {
  min-width: 300px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 0.9375rem;
  color: var(--app-text-secondary);
}

.summary-item .free {
  color: #27ae60;
  font-weight: 500;
}

.summary-divider {
  height: 1px;
  background: var(--app-border-light);
  margin: 8px 0;
}

.summary-item.total {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--app-text-primary);
}

.summary-item.total span:last-child {
  color: var(--app-color-primary);
  font-size: 1.375rem;
}

.submit-order-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 16px 40px !important;
  font-size: 1.0625rem !important;
  font-weight: 600 !important;
  border-radius: var(--app-radius-lg) !important;
  background: var(--app-gradient-primary) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(255, 126, 126, 0.35) !important;
}

.submit-order-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 126, 126, 0.5) !important;
}

.submit-order-btn:disabled {
  opacity: 0.5;
}

@media (max-width: 768px) {
  .summary-inner {
    flex-direction: column;
    gap: 20px;
    align-items: stretch;
  }

  .submit-order-btn {
    width: 100%;
    justify-content: center;
  }

  .address-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .default-tag {
    margin-left: 0;
  }
}
</style>
